import { isDefined } from "@clipboard-health/util-ts";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInMinutes, parseISO } from "date-fns";
import { useMemo } from "react";

/**
 * Shift details for the support flow.
 * Only contains active shifts where the worker remains assigned.
 * Cancelled/deleted shifts are filtered out at the list level.
 */
export interface SupportShiftDetails {
  readonly id: string;
  readonly workplaceId: string;
  readonly workplaceName: string;
  readonly workplaceAddress: string | undefined;
  readonly workplaceTimezone: string;
  readonly startAt: string;
  readonly endAt: string;
  /** Scheduled duration in hours (from start/end times) */
  readonly scheduledDurationHours: number | undefined;
  /** Actual worked duration in hours (from clock in/out, only for past shifts) */
  readonly actualDurationHours: number | undefined;
  readonly qualificationName: string | undefined;
  /**
   * Gross earnings in cents before any fees (from originalAmount).
   * Converted from API dollars to cents to avoid floating-point issues in downstream math.
   */
  readonly grossEarningsCents: number | undefined;
  /**
   * Hourly rate in cents from shift data.
   * Converted from API dollars to cents for consistent unit handling across all money values.
   */
  readonly hourlyRateCents: number | undefined;
  readonly clockInTime: string | undefined;
  readonly clockOutTime: string | undefined;
  readonly lunchStartTime: string | undefined;
  readonly lunchEndTime: string | undefined;
  readonly isVerified: boolean;
  readonly isInstantPay: boolean;
}

interface UseShiftDetailsParams {
  readonly shiftId: string;
}

export function useShiftDetails(params: UseShiftDetailsParams) {
  const { shiftId } = params;

  const query = useGetWorkerShift(shiftId, {
    enabled: Boolean(shiftId),
  });

  const shiftDetails = useMemo(() => {
    if (!query.data?.response) {
      return undefined;
    }

    return transformWorkerShiftToDetails(query.data.response);
  }, [query.data?.response]);

  return {
    data: shiftDetails,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

function transformWorkerShiftToDetails(shift: WorkerShift): SupportShiftDetails {
  const { facility } = shift;
  const workplaceAddress = formatWorkplaceAddress(facility.fullAddress);

  // Compute scheduled duration from start/end times
  const scheduledDurationHours = computeScheduledDurationHours(shift);
  // Actual worked hours from clock in/out (only populated for past shifts)
  const actualDurationHours = nullToUndefined(shift.time);

  // Use originalAmount for gross earnings (total pay before InstantPay fees)
  // Convert dollars to cents ONCE here to avoid floating-point issues in downstream math
  const grossEarningsCents = dollarsToCents(nullToUndefined(shift.originalAmount));
  // Convert hourly rate to cents for consistent unit handling
  const hourlyRateCents = dollarsToCents(nullToUndefined(shift.finalPay));

  return {
    id: shift._id,
    workplaceId: shift.facilityId,
    workplaceName: facility.name,
    workplaceAddress,
    workplaceTimezone: facility.tmz ?? DEFAULT_TIMEZONE,
    startAt: shift.start,
    endAt: shift.end,
    scheduledDurationHours,
    actualDurationHours,
    qualificationName: shift.agentReq,
    grossEarningsCents,
    hourlyRateCents,
    clockInTime: nullToUndefined(shift.clockInOut?.start),
    clockOutTime: nullToUndefined(shift.clockInOut?.end),
    lunchStartTime: nullToUndefined(shift.lunchInOut?.start),
    lunchEndTime: nullToUndefined(shift.lunchInOut?.end),
    isVerified: shift.verified ?? false,
    isInstantPay: shift.isInstantPay,
  };
}

function formatWorkplaceAddress(
  fullAddress: WorkerShift["facility"]["fullAddress"]
): string | undefined {
  if (!fullAddress) {
    return undefined;
  }

  // Use line1 if available, otherwise construct from streetNumber + streetName
  const streetAddress =
    fullAddress.line1 ??
    [fullAddress.streetNumber, fullAddress.streetName].filter(isDefined).join(" ");

  const parts = [streetAddress, fullAddress.city, fullAddress.state, fullAddress.postalCode].filter(
    (part) => isDefined(part) && part.length > 0
  );

  return parts.length > 0 ? parts.join(", ") : undefined;
}

const MINUTES_PER_HOUR = 60;

function computeScheduledDurationHours(shift: WorkerShift): number {
  // Use minutes for precision, then convert to hours
  // This preserves fractional hours (e.g., 10.5 for 10h 30m)
  const minutes = differenceInMinutes(parseISO(shift.end), parseISO(shift.start));
  return minutes / MINUTES_PER_HOUR;
}

function nullToUndefined<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

/**
 * Converts dollars to cents with rounding to handle floating-point precision issues.
 * Returns undefined if the input is undefined.
 */
function dollarsToCents(dollars: number | undefined): number | undefined {
  if (dollars === undefined) {
    return undefined;
  }
  return Math.round(dollars * 100);
}
